import { bufferedParserFetchEffect } from './http';
import { Effect, Layer, Option } from 'effect';
import { PDFDocument } from 'pdf-lib';

import {
  DocumentSource,
  makeParserDependencyError,
  PageSource,
  ParserHttp,
  ParserSecrets,
  ParserTracer,
  PdfTextLayer,
  bytesRef,
  makeLangfuseParserTracerService,
  type PageRegionClip,
  type ParseDocumentRef,
  type ParserDependencyError,
  type PdfTextLayerPage,
  type TextItem,
} from '../parser-host';
import { runParseDocument, type ParserDefinition } from '../parser-layer';
import { toOkraParseOutput } from '../projections/okra-parse-output';
import { cropPdfPageToRegionEffect } from './pdf-crop';

export interface CloudflareFetcher {
  readonly fetch: (input: string | URL, init?: RequestInit) => Promise<Response>;
}

export interface ParserCloudflareEnv {
  readonly PDF_RENDER_AGENT?: CloudflareFetcher;
  readonly [key: string]: unknown;
}

export interface R2ObjectBodyLike {
  readonly arrayBuffer: () => Promise<ArrayBuffer>;
}

export interface R2BucketLike {
  readonly get: (key: string) => Promise<R2ObjectBodyLike | null>;
}

export interface ParserHostCloudflareOptions {
  readonly allowedHosts: readonly string[];
  readonly fetch?: typeof globalThis.fetch;
  /** R2 binding used to materialize `{ kind: 'storage', provider: 'r2' }` refs. */
  readonly r2Bucket?: R2BucketLike;
}

function failure(
  service: 'http' | 'page-source' | 'text-layer' | 'document-source',
  operation: string,
  code: string,
  retryable: boolean,
  cause?: unknown,
) {
  return makeParserDependencyError({ service, operation, code, retryable }, cause);
}

function isAllowedHost(url: URL, allowedHosts: readonly string[]): boolean {
  return allowedHosts.some((host) => {
    const normalized = host.trim().toLowerCase();
    if (normalized.length === 0) return false;
    const hostname = url.hostname.toLowerCase();
    if (normalized.startsWith('*.')) {
      const suffix = normalized.slice(2);
      return hostname === suffix || hostname.endsWith(`.${suffix}`);
    }
    return hostname === normalized;
  });
}

async function splitPdf(bytes: Uint8Array, page: number): Promise<Uint8Array> {
  const source = await PDFDocument.load(bytes, { ignoreEncryption: true });
  if (!Number.isInteger(page) || page < 1 || page > source.getPageCount()) {
    throw new RangeError('page is outside the document');
  }
  const output = await PDFDocument.create();
  const [copied] = await output.copyPages(source, [page - 1]);
  output.addPage(copied);
  return output.save();
}

interface UnpdfTextPage {
  readonly getViewport: (options: { readonly scale: number }) => {
    readonly width: number;
    readonly height: number;
  };
  readonly getTextContent: () => Promise<{ readonly items: readonly unknown[] }>;
}

interface UnpdfDocument {
  readonly getPage: (page: number) => Promise<UnpdfTextPage>;
  readonly destroy?: () => Promise<void>;
}

function decodeTextItem(item: unknown): TextItem | undefined {
  if (typeof item !== 'object' || item === null || !('str' in item)) return undefined;
  const candidate = item as {
    readonly str?: unknown;
    readonly width?: unknown;
    readonly height?: unknown;
    readonly transform?: unknown;
  };
  if (typeof candidate.str !== 'string') return undefined;
  return {
    str: candidate.str,
    ...(typeof candidate.width === 'number' ? { width: candidate.width } : {}),
    ...(typeof candidate.height === 'number' ? { height: candidate.height } : {}),
    ...(Array.isArray(candidate.transform) &&
    candidate.transform.every((value) => typeof value === 'number')
      ? { transform: candidate.transform }
      : {}),
  };
}

async function extractTextPage(bytes: Uint8Array, page: number): Promise<PdfTextLayerPage> {
  const unpdf = await import('unpdf');
  const proxy = (await unpdf.getDocumentProxy(bytes)) as unknown as UnpdfDocument;
  try {
    const pdfPage = await proxy.getPage(page);
    const viewport = pdfPage.getViewport({ scale: 1 });
    const content = await pdfPage.getTextContent();
    return {
      items: content.items.flatMap((item) => {
        const decoded = decodeTextItem(item);
        return decoded ? [decoded] : [];
      }),
      width: viewport.width,
      height: viewport.height,
    };
  } finally {
    await proxy.destroy?.();
  }
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
}

export function parserHostCloudflare(
  env: ParserCloudflareEnv,
  options: ParserHostCloudflareOptions,
): Layer.Layer<
  ParserHttp | PageSource | PdfTextLayer | ParserSecrets | DocumentSource | ParserTracer
> {
  const fetchImplementation = options.fetch ?? globalThis.fetch;
  const http = ParserHttp.of({
    fetch: (input, init) => {
      let url: URL;
      try {
        url = input instanceof URL ? input : new URL(input);
      } catch (cause) {
        return Effect.fail(failure('http', 'fetch', 'invalid_url', false, cause));
      }
      if (!isAllowedHost(url, options.allowedHosts)) {
        return Effect.fail(failure('http', 'fetch', 'host_not_allowed', false));
      }
      return bufferedParserFetchEffect(fetchImplementation, url, init, (cause, stage) =>
        failure(
          'http',
          'fetch',
          stage === 'body' ? 'response_read_failed' : 'request_failed',
          stage === 'request' && init?.signal?.aborted !== true,
          cause,
        ),
      );
    },
  });
  const resolveBytes = (
    document: ParseDocumentRef,
  ): Effect.Effect<Uint8Array, ParserDependencyError> => {
    if (document.kind !== 'storage') return Effect.succeed(document.bytes);
    if (document.provider !== 'r2' || options.r2Bucket === undefined) {
      return Effect.fail(
        makeParserDependencyError({
          service: 'document-source',
          operation: 'bytes',
          code: 'provider_unsupported',
          retryable: false,
        }),
      );
    }
    const bucket = options.r2Bucket;
    return Effect.tryPromise({
      try: async () => {
        const object = await bucket.get(document.key);
        if (!object) throw new Error('R2 object not found');
        return new Uint8Array(await object.arrayBuffer());
      },
      catch: (cause) => failure('document-source', 'r2-get', 'object_unavailable', false, cause),
    });
  };
  const pageSource = PageSource.of({
    countPages: (document) =>
      document.pageCount !== undefined
        ? Effect.succeed(document.pageCount)
        : resolveBytes(document).pipe(
            Effect.flatMap((bytes) =>
              Effect.tryPromise({
                try: async () =>
                  (await PDFDocument.load(bytes, { ignoreEncryption: true })).getPageCount(),
                catch: (cause) =>
                  failure('page-source', 'count-pages', 'invalid_pdf', false, cause),
              }),
            ),
          ),
    pagePdf: (document, page, clip?: PageRegionClip) =>
      resolveBytes(document).pipe(
        Effect.flatMap((bytes) =>
          Effect.tryPromise({
            try: () => splitPdf(bytes, page),
            catch: (cause) => failure('page-source', 'page-pdf', 'invalid_page', false, cause),
          }),
        ),
        Effect.flatMap((bytes) =>
          clip?.region === undefined
            ? Effect.succeed(bytes)
            : cropPdfPageToRegionEffect(bytes, clip.region),
        ),
      ),
    // PDF_RENDER_AGENT currently renders designed PDFs and does not expose a
    // source-PDF page raster endpoint. Keep the binding in the environment
    // contract for a future opt-in implementation, but never call an invented
    // internal route.
    pageImage: () => Effect.fail(failure('page-source', 'page-image', 'unsupported', false)),
  });
  const textLayer = PdfTextLayer.of({
    extractPage: (document, page) =>
      resolveBytes(document).pipe(
        Effect.flatMap((bytes) =>
          Effect.tryPromise({
            try: () => extractTextPage(bytes, page),
            catch: (cause) => failure('text-layer', 'extract-page', 'extract_failed', false, cause),
          }),
        ),
      ),
  });
  const secrets = ParserSecrets.of({
    get: (name) =>
      Effect.succeed(Option.fromNullishOr(typeof env[name] === 'string' ? env[name] : undefined)),
  });
  const documentSource = DocumentSource.of({ bytes: resolveBytes });
  const tracer = ParserTracer.of(
    makeLangfuseParserTracerService({
      getSecret: (name) => (typeof env[name] === 'string' ? (env[name] as string) : undefined),
      fetch: fetchImplementation,
    }),
  );
  return Layer.mergeAll(
    Layer.succeed(ParserHttp, http),
    Layer.succeed(PageSource, pageSource),
    Layer.succeed(PdfTextLayer, textLayer),
    Layer.succeed(ParserSecrets, secrets),
    Layer.succeed(DocumentSource, documentSource),
    Layer.succeed(ParserTracer, tracer),
  );
}

export interface ParserWorkerRequest {
  readonly pdfBase64?: string;
  readonly pdfUrl?: string;
  readonly fileName?: string;
  readonly providerOptions?: Readonly<Record<string, unknown>>;
}

/** Derived Worker fetch adapter for ParseRequest -> ParseResult. */
export function parserWorkerHandler<R>(
  definition: ParserDefinition<R>,
  host: Layer.Layer<R>,
): (request: Request) => Promise<Response> {
  return async (request) => {
    if (request.method !== 'POST') {
      return new Response(JSON.stringify({ error: 'method_not_allowed' }), {
        status: 405,
        headers: { 'content-type': 'application/json' },
      });
    }
    try {
      const input = (await request.json()) as ParserWorkerRequest;
      let pdfBase64 = input.pdfBase64;
      if ((!pdfBase64 || pdfBase64.length === 0) && input.pdfUrl) {
        const source = await globalThis.fetch(input.pdfUrl, {
          redirect: 'follow',
          signal: request.signal,
        });
        if (!source.ok) throw new Error('PDF source returned an unsuccessful response');
        pdfBase64 = toBase64(new Uint8Array(await source.arrayBuffer()));
      }
      if (typeof pdfBase64 !== 'string' || pdfBase64.length === 0) {
        return new Response(JSON.stringify({ error: 'pdfBase64_required' }), {
          status: 400,
          headers: { 'content-type': 'application/json' },
        });
      }
      const startedAt = Date.now();
      const result = await Effect.runPromise(
        runParseDocument(
          definition,
          {
            document: bytesRef({
              documentId: crypto.randomUUID(),
              bytes: fromBase64(
                pdfBase64.replace(/^data:application\/pdf;base64,/, '').replace(/\s+/g, ''),
              ),
              ...(input.fileName === undefined ? {} : { fileName: input.fileName }),
              mimeType: 'application/pdf',
            }),
            ...(input.providerOptions === undefined ? {} : { options: input.providerOptions }),
          },
          host,
        ),
      );
      const durationMs = Date.now() - startedAt;
      const worker = result.rawPages?.find(
        (raw) => typeof raw === 'object' && raw !== null && 'worker' in raw,
      ) as
        | {
            readonly worker?: {
              readonly summary?: { readonly pages: number; readonly costUsd: number };
              readonly metadata?: Record<string, unknown>;
            };
          }
        | undefined;
      return Response.json({
        parserId: definition.spec.id,
        output: toOkraParseOutput(result),
        summary: {
          pages: worker?.worker?.summary?.pages ?? result.pages.length,
          durationMs,
          costUsd: worker?.worker?.summary?.costUsd ?? 0,
        },
        metadata: worker?.worker?.metadata ?? { parser: definition.spec.id },
      });
    } catch {
      return Response.json(
        { error: 'parser_failed', message: 'parser execution failed' },
        { status: 502 },
      );
    }
  };
}
